import { get, set, del, keys } from 'idb-keyval';
import type { DailyProgress, DateKey, Id, SyncedTree } from '../types';
import { runMigrations } from './migrations';

// Single key for the family-wide synced tree.
const SYNCED_TREE_KEY = 'aamusankari:syncedTree';

// Per-day progress is keyed by `${profileId}_${dateKey}`. Prefixed so we can
// list/clear only progress keys without touching the synced tree.
const PROGRESS_PREFIX = 'aamusankari:progress:';
const progressKey = (profileId: Id, dateKey: DateKey): string =>
  `${PROGRESS_PREFIX}${profileId}_${dateKey}`;

export async function loadSyncedTree(): Promise<SyncedTree | null> {
  const raw = await get<SyncedTree>(SYNCED_TREE_KEY);
  if (!raw) return null;
  return runMigrations(raw);
}

export async function saveSyncedTree(tree: SyncedTree): Promise<void> {
  await set(SYNCED_TREE_KEY, tree);
}

export async function loadDailyProgress(
  profileId: Id,
  dateKey: DateKey,
): Promise<DailyProgress | null> {
  const raw = await get<DailyProgress>(progressKey(profileId, dateKey));
  return raw ?? null;
}

export async function saveDailyProgress(progress: DailyProgress): Promise<void> {
  await set(progressKey(progress.profileId, progress.dateKey), progress);
}

export async function deleteDailyProgress(profileId: Id, dateKey: DateKey): Promise<void> {
  await del(progressKey(profileId, dateKey));
}

// List all stored progress keys (for cleanup / archival, see plan/02 Q4).
export async function listProgressKeys(): Promise<string[]> {
  const all = await keys();
  return all
    .filter((k): k is string => typeof k === 'string')
    .filter((k) => k.startsWith(PROGRESS_PREFIX));
}
